import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { TransferOrgFundsApiArg } from '@/__generated/es-api'
import { selectToken } from '@/app/module/auth/store/selectors'
import { createNotification } from '@/app/module/notifications/store/actions'
import { ERROR_TRANSFER_FUNDS, SUCCESS_TRANSFER_FUNDS } from '@/app/module/payments/definitions'
import { getUser } from '@/app/module/user/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

/**
 * Hook to transfer funds from the active organization to another organization
 */
export const useTransferFunds = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const token = useSelector(selectToken)

  const [mutate, res] = esApi.endpoints.transferOrgFunds.useMutation()

  const transferFunds = useCallback(
    async (body: TransferOrgFundsApiArg['body']) => {
      try {
        await mutate({ orgId: orgId.toString(), body }).unwrap()

        dispatch(
          createNotification({
            'transfer-funds-success': {
              type: 'success',
              message: SUCCESS_TRANSFER_FUNDS,
            },
          }),
        )

        dispatch(getUser({ token }))
        return true
      } catch {
        dispatch(
          createNotification({
            'transfer-funds-error': {
              type: 'error',
              message: ERROR_TRANSFER_FUNDS,
            },
          }),
        )
        return false
      }
    },
    [dispatch, mutate, orgId, token],
  )

  return [transferFunds, res] as const
}
